import React, {
  ReactNode, 
  useMemo,
  useCallback,
} from "react";

import {
  AnalyticsIcon,
  SearchIcon
} from "../icons";

import { 
  Container, 
  ScreenContainer, 
  ScreenHeader 
} from '../wombat-ui';

import { 
  Accordion,
  VStack,
  HStack, 
  Input,
  Button,
  ButtonGroup,
  Stack,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";

import {
  Select,
} from "chakra-react-select";

import useTranslations from "../../hooks/useTranslations";
import useFuzzySearch from '../../hooks/useFuzzySearch';
import useViewer from "../../hooks/useViewer";
  
import { 
  groupBy, 
  sumBy,
} from 'lodash';
  
import { 
  useGetQuestions, 
  useGetAnswersForMapping, 
  QuestionMinimalFragment
} from '../../graphql/generated/definitions';

import {
  NumberParam,
  StringParam,
  BooleanParam,
  useQueryParams,
  withDefault,
  ArrayParam,
} from "use-query-params";

import AnalyticsQuestionItem from "./AnalyticsQuestionItem";
import ResourceProgress from "../utils/ResourceProgress";
import Pagination from "../utils/Pagination";
import SelectNoOptionsMessage from "../utils/SelectNoOptionsMessage";

type AnswerMappingProps = {
  title: string | ReactNode,
  description: string | ReactNode,
  questionTypes: string[],
  defaultIsCollaborator: boolean,
  buildUrl?: (question: QuestionMinimalFragment) => string
};

const AnswerMapping: React.FC<AnswerMappingProps> = ({
  title,
  description,
  questionTypes,
  defaultIsCollaborator,
  buildUrl
}) => {
  const t = useTranslations();

  const [{ q, selectedGroupIds, isCollaborator, page, per }, setQuery] =
    useQueryParams({
      selectedGroupIds: withDefault(ArrayParam, []),
      isCollaborator: withDefault(BooleanParam, defaultIsCollaborator),
      q: withDefault(StringParam, ""),
      page: withDefault(NumberParam, 1),
      per: withDefault(NumberParam, 30),
    });

  const {
    company: { usersCount, groups },
  } = useViewer();

  const groupsOptions = groups.map((group) => {
    return {label: group.name, value: group.id, id: group.id}
  });

  const groupsOptionsDefaultValue = useMemo(() =>
    groupsOptions.filter((option) => selectedGroupIds.includes(option.value)), 
  [groupsOptions, selectedGroupIds]);

  const selectGroupsStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: 220,
      maxWidth: 300,
    })
  };

  const { data: questionsData, loading: questionsDataLoading } =
    useGetQuestions({
      variables: {
        type: questionTypes,
        havingAnswers: true,
        distinctByDigest: true
      },
    });

  const questions = questionsData?.questions?.collection || [];

  const searchFiltered = useFuzzySearch(questions, q, {
    keys: ["label"],
    threshold: 0.3,
    useExtendedSearch: true,
    matchIfEmpty: true,
  }).map(({ item }) => item);

  const { data: answersData, loading: answersDataLoading } = useGetAnswersForMapping({
    variables: {
      questionDigest: questions.map(({digest}) => digest),
      lastCreatedAt: 'now',
      groupIds: selectedGroupIds,
      isCollaborator
    },
    skip: searchFiltered.length === 0
  });

  const answers = answersData?.answers?.collection || [];

  const answersByQuestionDigest = useMemo(() => (
    groupBy(answers, 'questionDigest')
  ), [answers]);

  const questionsWithAnswers = useMemo(() => (
    searchFiltered
      .filter((question) => answersByQuestionDigest[question.digest])
  ), [searchFiltered, answersByQuestionDigest]);

  const paginatedQuestionsWithAnswers = useMemo(() => (
    questionsWithAnswers.slice((page - 1) * per, page * per)
  ), [questionsWithAnswers, page, per]);

  const populationCount = useMemo(() => {
    if (selectedGroupIds.length != 0) {
      const populationCountInFilteredGroups = sumBy(
        groups.filter((group) => selectedGroupIds.includes(group.id)),
        'usersCount'
      )

      return (populationCountInFilteredGroups);
    }
    return (usersCount);
  }, [usersCount, selectedGroupIds, groups]);

  const handleSearchChange = useCallback((e) => {
    setQuery({ q: e.target.value, page: 1 })
  }, []);

  const handleGroupSelect = useCallback((values) => {
    setQuery({ 
      selectedGroupIds: values.map((item) => item.value),
      page: 1
    })
  }, []);

  return (
    <ScreenContainer>
      <ScreenHeader
        title={title}
        description={description}
        icon={AnalyticsIcon}
      />

      <Container>
        <VStack
          align="stretch"
        >
          <HStack 
            justifyContent="space-between"
            wrap="wrap"
            spacing={5}
            shouldWrapChildren
          >
            <Stack spacing={4}>
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  children={<SearchIcon className="w-5 h-5" />}
                />
                <Input
                  data-test-id="analytics-toolbar-searchbar-input"
                  placeholder={t("statistics.search.default").toString()}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Stack>

            <HStack
              shouldWrapChildren
            >
              <ButtonGroup
                isAttached
              >
                <Button
                  mr="-px"
                  fontWeight="medium"
                  colorScheme={isCollaborator ? "blue" : undefined}
                  onClick={() => setQuery({ isCollaborator: true, page: 1 })}
                >
                  {t("statistics.filters.collaborators")}
                </Button>
                <Button
                  mr="-px"
                  fontWeight="medium"
                  colorScheme={!isCollaborator ? "blue" : undefined}
                  onClick={() => setQuery({ isCollaborator: false, page: 1 })}
                >
                  {t("statistics.filters.referents")}
                </Button>
              </ButtonGroup>

              <Select
                isMulti
                id="analytics-toolbar-groups-select"
                placeholder={t("statistics.filters.byGroups").toString()}
                onChange={handleGroupSelect}
                width="220px"
                defaultValue={groupsOptionsDefaultValue}
                options={groupsOptions}
                styles={selectGroupsStyles}
                components={{ NoOptionsMessage: SelectNoOptionsMessage }}
              >
              </Select>
            </HStack>
          </HStack>
          <ResourceProgress
            isLoading={questionsDataLoading || answersDataLoading}
            isEmpty={paginatedQuestionsWithAnswers.length === 0}
            emptyMessage={t("statistics.notEnoughData") as string}
          />
          <Accordion allowToggle>
            {
              paginatedQuestionsWithAnswers.map((question) => (
                <AnalyticsQuestionItem
                  key={question.id}
                  href={buildUrl ? buildUrl(question) : null}
                  question={question}
                  answers={answersByQuestionDigest[question.digest]}
                  type={questionTypes[0]}
                  populationCount={populationCount}
                />
              ))
            }
          </Accordion>
          {
            questionsWithAnswers.length > 0 && (
              <Pagination
                page={page}
                itemsPerPage={per}
                itemsCount={questionsWithAnswers.length}
                onChange={(page) => setQuery({ page })}
              />
            )
          }
        </VStack>
      </Container>
    </ScreenContainer>
  );
}

export default React.memo(AnswerMapping);